/**
 * Typed wrappers over the HTTP tool gateway for the `/lineup/:matchId`
 * editor (web UI Phase 2 — lineup editor chunk 1/2).
 *
 * The SPA NEVER calls the MCP server or Supabase directly. Every call
 * here goes through `POST /api/tool/:tool_name` (the gateway), which
 * forces `team_id` from the JWT, role-checks, rate-limits, and audits.
 *
 * Gateway response shapes (see `src/api/tool-gateway.ts`):
 *   - Success → 200 `{ ok: true, result: <MCP rpc.result> }` where
 *     `result` is EITHER the unwrapped tool payload `{ ok: true, ... }`
 *     (contract/test shape) OR an MCP SDK content envelope
 *     `{ content: [{ type: "text", text: "<json>" }] }` (prod shape).
 *     `unwrapToolPayload` below normalizes both — ported verbatim from
 *     `src/bot/dispatcher.ts` so web + bot agree on the unwrap.
 *   - Tool/usage error → 502 `{ ok: false, error: { code, message } }`.
 *     **The gateway deliberately scrubs the real MCP error text** and
 *     replaces it with a generic "Internal error processing tool call",
 *     preserving only `error.code`. So `WTTA_DESCENDING_VIOLATED`,
 *     `NO_APPROVED_LINEUP`, etc. arrive as a `code`, not a message — we
 *     map known codes to captain-readable copy in the page.
 *
 * No retry / no caching — the page owns those policies (it doesn't need
 * either for a one-shot human-driven flow).
 */

import { ApiError, apiPost } from "./api";

// ─── Wire types — mirror the MCP tool output contracts ─────────────────
//
// Kept narrow + local; these are the shapes the page renders, not the
// full server interfaces. See `src/mcp/tools/{propose,approve,
// get_lineup_card}.ts` for the authoritative definitions.

export type ProposalKind = "straight" | "stacked" | "edited";

export type PlayerRef = { id: string; name: string; ntrp: number };

export type ProposedLine = {
  line_number: 1 | 2 | 3 | 4;
  player1: PlayerRef;
  player2: PlayerRef;
  combined_ntrp: number;
  rationale: string;
  projected_rating_impact?: number;
};

export type ProposedLineup = {
  lineup_id: string;
  proposal_kind: "straight" | "stacked";
  lines: ProposedLine[];
};

export type LineupProposals = {
  ok: true;
  proposals: ProposedLineup[];
  scouting_available: boolean;
};

export type ApprovedLine = {
  line_number: 1 | 2 | 3 | 4;
  player1: PlayerRef;
  player2: PlayerRef;
  combined_ntrp: number;
};

export type ApprovedLineup = {
  ok: true;
  lineup_id: string;
  version: number;
  state: "approved";
  proposal_kind: ProposalKind;
  idempotent?: boolean;
  lines: ApprovedLine[];
};

export type LineupCardLine = {
  line_number: 1 | 2 | 3 | 4;
  player1: { id: string; name: string };
  player2: { id: string; name: string };
  combined_ntrp: number | null;
};

export type LineupCard = {
  ok: true;
  lineup_id: string;
  version: number;
  proposal_kind: ProposalKind;
  card_markdown: string;
  lines: LineupCardLine[];
};

export type LineupEdit = {
  line_number: 1 | 2 | 3 | 4;
  player1_id?: string;
  player2_id?: string;
};

/**
 * Normalized error a page can branch on. `code` is the MCP/gateway error
 * code that survived the gateway scrub (e.g. `WTTA_DESCENDING_VIOLATED`,
 * `NO_APPROVED_LINEUP`, `BAD_INPUT`); `httpStatus` is the gateway HTTP
 * status (404 = role/tool denied, 401 = unauthed, 429 = rate-limited).
 */
export class LineupToolError extends Error {
  code: string;
  httpStatus: number;
  constructor(code: string, httpStatus: number, message: string) {
    super(message);
    this.name = "LineupToolError";
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

// ─── Gateway unwrap (ported from src/bot/dispatcher.ts) ─────────────────

// Exported so the sibling `score-api.ts` reuses the SAME unwrap (web +
// bot must agree on the envelope normalization — no second copy).
export function unwrapToolPayload(
  result: unknown,
): Record<string, unknown> | null {
  if (typeof result !== "object" || result === null) return null;
  const r = result as Record<string, unknown>;
  // Already unwrapped (test/contract shape).
  if ("ok" in r) return r;
  // MCP SDK content envelope.
  const content = r.content;
  if (Array.isArray(content) && content.length > 0) {
    const first = content[0] as Record<string, unknown> | undefined;
    if (first && typeof first.text === "string") {
      try {
        const parsed = JSON.parse(first.text);
        return typeof parsed === "object" && parsed !== null
          ? (parsed as Record<string, unknown>)
          : null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * POST a tool through the gateway and return the unwrapped tool payload.
 *
 * Throws `LineupToolError` on:
 *   - gateway HTTP non-2xx (401 / 404 / 429 / 502) — the gateway's own
 *     `error.code` (or a synthetic one) is carried through.
 *   - a 200 whose unwrapped tool payload is itself `{ ok: false, error }`
 *     (a tool-level failure the gateway passed through as a 200 — rare,
 *     but the contract shape allows it).
 */
async function callTool<T>(
  tool: string,
  args: Record<string, unknown>,
): Promise<T> {
  let body: { ok: true; result: unknown };
  try {
    body = await apiPost<{ ok: true; result: unknown }>(
      `/api/tool/${tool}`,
      args,
    );
  } catch (e) {
    if (e instanceof ApiError) {
      // The gateway error body is `{ ok:false, error: {code,message} }`
      // for 502, or `{ ok:false, error: "<string>" }` for 401/404/429.
      const b = e.body as
        | { error?: unknown }
        | null
        | undefined;
      let code = "GATEWAY_ERROR";
      if (b && typeof b === "object" && "error" in b) {
        const err = (b as { error: unknown }).error;
        if (typeof err === "string") code = err.toUpperCase();
        else if (err && typeof err === "object" && "code" in err) {
          const c = (err as { code?: unknown }).code;
          if (typeof c === "string") code = c;
        }
      }
      throw new LineupToolError(code, e.status, e.message);
    }
    throw new LineupToolError("NETWORK", 0, "Network error. Retry.");
  }

  const payload = unwrapToolPayload(body.result);
  if (!payload) {
    throw new LineupToolError(
      "MALFORMED_RESPONSE",
      200,
      "The server returned an unreadable response.",
    );
  }
  if (payload.ok === false) {
    const err = payload.error as
      | { code?: unknown; message?: unknown }
      | undefined;
    const code =
      err && typeof err.code === "string" ? err.code : "TOOL_ERROR";
    const message =
      err && typeof err.message === "string"
        ? err.message
        : "The tool reported an error.";
    throw new LineupToolError(code, 200, message);
  }
  return payload as T;
}

// ─── Public API ────────────────────────────────────────────────────────

/** Generate the straight + stacked draft proposals for a match. */
export function proposeLineup(matchId: string): Promise<LineupProposals> {
  return callTool<LineupProposals>("propose_lineup", { match_id: matchId });
}

/** Read the current approved lineup card for a match (if any). */
export function getLineupCard(matchId: string): Promise<LineupCard> {
  return callTool<LineupCard>("get_lineup_card", { match_id: matchId });
}

/**
 * Approve a lineup — the EXPLICIT human action (AGENTS.md rule 2). The
 * gateway has no auto-approve path; this only fires from the captain
 * tapping "Approve" in the editor.
 */
export function approveLineup(
  lineupId: string,
  edits: LineupEdit[],
): Promise<ApprovedLineup> {
  return callTool<ApprovedLineup>("approve_lineup", {
    lineup_id: lineupId,
    ...(edits.length > 0 ? { edits } : {}),
  });
}
